import { useEffect, useState, useRef } from 'react';
import { Upload, Download, Trash2, FileText, Box, ClipboardCheck, RefreshCw, ChevronDown, Sparkles, Image as ImageIcon } from 'lucide-react';
import { rfqAPI } from '@/services/api';
import { t } from '@/i18n/translations';
import { formatApiError } from '@/lib/apiError';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import QuotationSuggestionsPanel from '@/components/QuotationSuggestionsPanel';

const SLOTS = [
  { id: 'model_3d', icon: Box, labelKey: 'slot_model_3d', hintKey: 'slot_hint_3d' },
  // 2D de Injeção — usado na extração de medidas.
  { id: 'drawing_2d', icon: FileText, labelKey: 'slot_drawing_2d', hintKey: 'slot_hint_2d' },
  // 2D de Maquinagem — informativo (sem extração de medidas).
  { id: 'drawing_2d_machining', icon: FileText, labelKey: 'slot_drawing_2d_machining', hintKey: 'slot_hint_2d' },
  // Slot "imagem peça" — foto/render da peça que é embebida no PDF da
  // proposta comercial gerada em "Outras Acções → Gerar PDF".
  { id: 'part_image', icon: ImageIcon, labelKey: 'slot_part_image', hintKey: 'slot_hint_part_image' },
  { id: 'additional', icon: ClipboardCheck, labelKey: 'slot_additional', hintKey: 'slot_hint_additional' },
];

/**
 * Sub-secção «Documentos essenciais (G1)» — 3 slots fixos com upload/replace/remove.
 * Para o slot drawing_2d expõe um <details> com a info extraída do PDF (lazy GET)
 * e, quando ``onApplySuggestions`` é fornecido, um painel de pré-preenchimento
 * que aplica auto a campos com confidence ≥ 0.85 e oferece os restantes como
 * pendentes para revisão.
 *
 * Props:
 *  - rfqId: number
 *  - documents: DocumentResponse[]
 *  - canEdit: bool — mostra botões Anexar/Substituir/Remover apenas quando true
 *  - onChanged: () => Promise — chamado depois de qualquer mutação para refresh
 *  - lang: string — pt|en|fr
 *  - alloys, rfq, quotation: passados ao QuotationSuggestionsPanel para
 *    construir as sugestões. Quando ausentes, o painel não é renderizado.
 *  - onApplySuggestions: (Array<Suggestion>) => void — chamado para aplicar
 *    sugestões à RFQ/Quotation. Quando ausente, o painel não é renderizado.
 *  - onMarkSuggested: (Array<string>) => void — paths aplicados (para badges)
 */
export default function EssentialDocumentsSection({
  rfqId,
  documents = [],
  canEdit = false,
  onChanged,
  lang = 'pt',
  alloys,
  rfq,
  quotation,
  onApplySuggestions,
  onMarkSuggested,
}) {
  const inputRefs = useRef({ model_3d: null, drawing_2d: null, drawing_2d_machining: null, part_image: null, additional: null });
  const [extraction, setExtraction] = useState(null);

  const docBySlot = SLOTS.reduce((acc, s) => {
    acc[s.id] = documents.find((d) => d.slot === s.id) || null;
    return acc;
  }, {});

  // Eager-load da extracção do desenho 2D — assim que aparece o doc no slot,
  // carregamos a extracção para podermos mostrar o painel de sugestões logo
  // após upload (sem o utilizador ter de abrir o painel "Info detectada").
  const drawingDoc = docBySlot.drawing_2d;
  useEffect(() => {
    let cancelled = false;
    if (!drawingDoc || !onApplySuggestions) {
      setExtraction(null);
      return undefined;
    }
    rfqAPI.getDrawingExtraction(rfqId, drawingDoc.id)
      .then((res) => {
        if (!cancelled) setExtraction(res.data);
      })
      .catch(() => {
        if (!cancelled) setExtraction(null);
      });
    return () => { cancelled = true; };
  }, [rfqId, drawingDoc?.id, onApplySuggestions]);

  const handleSelectFile = async (slot, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    // Para o desenho 2D corremos OCR em-linha (10–30s para desenhos
    // complexos). Mostramos um toast persistente para o utilizador
    // saber que o sistema está a trabalhar.
    const isPdfDrawing = slot === 'drawing_2d'
      && (file.type || '').toLowerCase().startsWith('application/pdf');
    const loadingId = isPdfDrawing
      ? toast.loading(t('extracting_drawing', lang))
      : null;
    try {
      await rfqAPI.uploadDocumentToSlot(rfqId, slot, file);
      if (loadingId) toast.dismiss(loadingId);
      toast.success(t('doc_attached', lang));
      if (onChanged) await onChanged();
    } catch (err) {
      if (loadingId) toast.dismiss(loadingId);
      const msg = formatApiError(err, t('error_attaching_doc', lang));
      toast.error(msg);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const blob = await rfqAPI.downloadDocument(rfqId, doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('error', lang));
    }
  };

  const handleRemove = async (doc) => {
    try {
      await rfqAPI.deleteDocument(rfqId, doc.id);
      if (onChanged) await onChanged();
    } catch {
      toast.error(t('error', lang));
    }
  };

  return (
    <div className="sub-surface space-y-2">
      <div className="sub-section-label">
        {t('g1_essential_documents', lang)}
      </div>
      <ul className="divide-y divide-border">
        {SLOTS.map((slot) => {
          const doc = docBySlot[slot.id];
          const Icon = slot.icon;
          return (
            <li key={slot.id} className="space-y-2 py-2">
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{t(slot.labelKey, lang)}</div>
                  <div className="text-xs text-muted-foreground">{t(slot.hintKey, lang)}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {doc ? (
                    <>
                      <span className="hidden max-w-[12rem] truncate text-xs text-muted-foreground sm:inline" title={doc.filename}>
                        {doc.filename}
                      </span>
                      <Button type="button" size="sm" variant="outline" onClick={() => handleDownload(doc)} aria-label={t('download', lang)}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      {canEdit && (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => inputRefs.current[slot.id]?.click()}
                          >
                            <Upload className="h-3.5 w-3.5" /> {t('replace', lang)}
                          </Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => handleRemove(doc)} aria-label={t('remove', lang)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-xs text-muted-foreground">{t('no_file', lang)}</span>
                      {canEdit && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => inputRefs.current[slot.id]?.click()}
                        >
                          <Upload className="h-3.5 w-3.5" /> {t('attach', lang)}
                        </Button>
                      )}
                    </>
                  )}
                  {canEdit && (
                    <input
                      ref={(el) => { inputRefs.current[slot.id] = el; }}
                      type="file"
                      data-slot={slot.id}
                      className="sr-only"
                      onChange={(e) => handleSelectFile(slot.id, e)}
                    />
                  )}
                </div>
              </div>

              {/* Painel de info detectada — apenas para o desenho 2D */}
              {slot.id === 'drawing_2d' && doc && (
                <>
                  <DrawingExtractionPanel
                    rfqId={rfqId}
                    doc={doc}
                    canEdit={canEdit}
                    lang={lang}
                    initialData={extraction}
                    onDataUpdated={setExtraction}
                  />
                  {onApplySuggestions && extraction && (
                    <QuotationSuggestionsPanel
                      extraction={extraction}
                      alloys={alloys || []}
                      rfq={rfq}
                      quotation={quotation}
                      canEdit={canEdit}
                      onApply={onApplySuggestions}
                      onMarkSuggested={onMarkSuggested}
                      lang={lang}
                    />
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DrawingExtractionPanel({ rfqId, doc, canEdit, lang, initialData, onDataUpdated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(initialData || null);
  const [error, setError] = useState(null);

  // Sincroniza com os dados pré-carregados pelo parent (eager-load).
  useEffect(() => {
    if (initialData) setData(initialData);
  }, [initialData]);

  const ensureLoaded = async (force = false) => {
    if (!force && data) return;
    setLoading(true);
    setError(null);
    try {
      const res = await rfqAPI.getDrawingExtraction(rfqId, doc.id);
      setData(res.data);
      onDataUpdated?.(res.data);
    } catch (err) {
      setError(formatApiError(err, 'extraction_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (e) => {
    const isOpen = e.target.open;
    setOpen(isOpen);
    if (isOpen) await ensureLoaded(false);
  };

  const handleReextract = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await rfqAPI.reextractDrawing(rfqId, doc.id);
      setData(res.data);
      onDataUpdated?.(res.data);
      toast.success(t('drawing_extracted_info', lang));
    } catch (err) {
      setError(formatApiError(err, 'extraction_error'));
    } finally {
      setLoading(false);
    }
  };

  const warnings = data?.warnings || [];
  const warningKey = (w) => {
    if (w === 'pdf_without_text_layer') return t('extraction_warning_no_text', lang);
    if (w === 'multipage_drawing') return t('extraction_warning_multipage', lang);
    if (w === 'unsupported_format_for_extraction') return t('extraction_warning_unsupported', lang);
    return w;
  };

  return (
    <details
      className="ml-6 rounded-md bg-muted/40 p-2 text-xs"
      onToggle={handleToggle}
      open={open}
    >
      <summary className="flex cursor-pointer items-center gap-2 select-none">
        <ChevronDown className="h-3.5 w-3.5 transition-transform" aria-hidden="true" />
        <Sparkles className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
        <span className="font-medium">{t('drawing_extracted_info', lang)}</span>
        <span className="text-muted-foreground">— {t('drawing_extracted_disclaimer', lang)}</span>
        {canEdit && open && (
          <button
            type="button"
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs hover:bg-accent"
            onClick={(e) => { e.preventDefault(); handleReextract(); }}
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            {t('reextract', lang)}
          </button>
        )}
      </summary>

      <div className="mt-2 space-y-3">
        {loading && <div className="text-muted-foreground">{t('extraction_loading', lang)}</div>}
        {error && <div className="text-destructive">{String(error)}</div>}
        {data && (
          <>
            {warnings.map((w) => (
              <div key={w} className="rounded-md border border-warning/40 bg-warning/10 p-2 text-warning-foreground">
                {warningKey(w)}
              </div>
            ))}
            {data.error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-destructive-foreground">
                {data.error}
              </div>
            )}

            <DrawingDataSection data={data} lang={lang} />
            <PieceMetadataSection data={data} lang={lang} />
            <DimensionsSection data={data} lang={lang} />
            <RawAdvancedSection data={data} lang={lang} />

            {!warnings.length &&
              !data.structured_fields &&
              !data.dimension_candidates?.length &&
              !data.error && (
                <div className="text-muted-foreground">{t('extraction_empty', lang)}</div>
              )}
          </>
        )}
      </div>
    </details>
  );
}

// ------- Secção 1: Dados gerais do desenho (formato, escala, datas, refs) -------
function DrawingDataSection({ data, lang }) {
  const sf = data.structured_fields || {};
  const pages = data.pages || [];
  const rev = data.revision_hints?.[0]?.text;

  const rows = [];
  if (sf.part_number) rows.push([t('field_part_number', lang), sf.part_number]);
  if (sf.designation) rows.push([t('field_designation', lang), sf.designation]);
  if (sf.scale_main) rows.push([t('field_scale', lang), sf.scale_main]);
  if (sf.page_format_iso) {
    const sz = pages[0]?.size_mm;
    const sizeStr = sz ? `${sz.width.toFixed(0)} × ${sz.height.toFixed(0)} mm` : '';
    rows.push([t('field_page_format', lang), `${sf.page_format_iso}${sizeStr ? ` (${sizeStr})` : ''}`]);
  }
  if (sf.step_file_ref) rows.push([t('field_step_ref', lang), sf.step_file_ref]);
  if (sf.drawn_date) rows.push([t('field_drawn_date', lang), sf.drawn_date]);
  if (rev) rows.push([t('field_revision', lang), rev]);

  if (!rows.length) return null;

  return (
    <Section title={t('section_drawing_data', lang)}>
      <KeyValueTable rows={rows} />
    </Section>
  );
}

// ------- Secção 2: Metadados da peça (peso, material, tolerâncias) -------
function PieceMetadataSection({ data, lang }) {
  const sf = data.structured_fields || {};
  const rows = [];
  if (sf.weight_kg != null) {
    rows.push([
      t('field_weight', lang),
      <span key="w">
        <strong>{sf.weight_kg.toFixed(3)} kg</strong>
        {sf.weight_raw && <span className="ml-2 text-muted-foreground">({sf.weight_raw})</span>}
      </span>,
    ]);
  }
  if (sf.material_full) {
    rows.push([
      t('field_material', lang),
      <span key="m">
        <strong>{sf.material_full}</strong>
        {sf.material_norm && sf.material_alloy && (
          <span className="ml-2 text-muted-foreground">
            ({sf.material_norm} · {sf.material_alloy})
          </span>
        )}
      </span>,
    ]);
  }
  if (sf.tolerance_standard) {
    rows.push([t('field_tolerance', lang), sf.tolerance_standard]);
  }

  if (!rows.length) return null;

  return (
    <Section title={t('section_piece_metadata', lang)}>
      <KeyValueTable rows={rows} />
    </Section>
  );
}

// ------- Secção 3: Candidatos a dimensões totais (com confidence) -------
function DimensionsSection({ data, lang }) {
  const candidates = data.dimension_candidates || [];
  if (!candidates.length) return null;

  const top3 = candidates.slice(0, 3);
  const rest = candidates.slice(3);

  return (
    <Section title={t('section_dimension_candidates', lang)}>
      <div className="text-[11px] text-muted-foreground mb-2">
        {t('dimension_candidates_disclaimer', lang)}
      </div>
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
        {top3.map((c) => (
          <DimensionPill key={`${c.value}-${c.rank}`} candidate={c} highlight lang={lang} />
        ))}
      </div>
      {rest.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[11px] text-muted-foreground hover:text-foreground">
            {t('show_more_candidates', lang)} (+{rest.length})
          </summary>
          <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-3">
            {rest.map((c) => (
              <DimensionPill key={`${c.value}-${c.rank}`} candidate={c} lang={lang} />
            ))}
          </div>
        </details>
      )}
    </Section>
  );
}

function DimensionPill({ candidate, highlight, lang }) {
  const conf = candidate.confidence ?? 0;
  let confColor = 'bg-muted text-muted-foreground';
  if (conf >= 0.8) confColor = 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  else if (conf >= 0.6) confColor = 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  else confColor = 'bg-muted text-muted-foreground';

  return (
    <div
      className={`flex items-center justify-between rounded-md border ${
        highlight ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border'
      } px-2 py-1`}
      title={candidate.reasons?.join(', ') || ''}
    >
      <div className="flex items-baseline gap-1">
        <span className="text-[10px] text-muted-foreground">#{candidate.rank}</span>
        <span className="font-mono text-sm tnum tabular-nums">
          {candidate.value.toFixed(candidate.value % 1 === 0 ? 0 : 1)}
        </span>
        <span className="text-[10px] text-muted-foreground">{candidate.unit || 'mm'}</span>
      </div>
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${confColor}`}>
        {Math.round(conf * 100)}%
      </span>
    </div>
  );
}

// ------- Secção 4: Modo avançado (raw output) -------
function RawAdvancedSection({ data, lang }) {
  return (
    <details className="rounded-md bg-muted/40 p-2">
      <summary className="cursor-pointer select-none text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
        {t('section_raw_advanced', lang)}
      </summary>
      <div className="mt-2 space-y-3">
        {data.pages?.length > 0 && (
          <SubSection title={t('extraction_pages', lang)}>
            <ul className="ml-4 list-disc">
              {data.pages.map((p) => (
                <li key={p.index}>
                  #{p.index + 1}: {p.size_mm?.width} × {p.size_mm?.height} mm
                </li>
              ))}
            </ul>
          </SubSection>
        )}

        {data.keyword_hits?.length > 0 && (
          <SubSection title={t('extraction_keyword_hits', lang)}>
            <table className="w-full text-left">
              <tbody>
                {data.keyword_hits.map((k, i) => (
                  <tr key={i} className="border-t border-border/50">
                    <td className="w-1/3 py-0.5 pr-2 font-medium">{k.keyword}</td>
                    <td className="py-0.5">{k.value || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SubSection>
        )}

        {data.numeric_tokens?.length > 0 && (
          <SubSection title={`${t('extraction_numeric_tokens', lang)} (${data.numeric_tokens.length})`}>
            <div className="flex flex-wrap gap-1">
              {data.numeric_tokens.slice(0, 80).map((n, i) => (
                <span key={i} className="rounded-md bg-background/70 px-1.5 py-0.5 tnum">
                  {n.raw}
                </span>
              ))}
              {data.numeric_tokens.length > 80 && (
                <span className="text-muted-foreground">+{data.numeric_tokens.length - 80}…</span>
              )}
            </div>
          </SubSection>
        )}

        {data.title_block_candidates?.length > 0 && (
          <SubSection title={t('extraction_title_block', lang)}>
            {data.title_block_candidates.map((tb, i) => (
              <pre key={i} className="whitespace-pre-wrap rounded-md bg-background/70 p-2 text-[11px]">
                {tb.text}
              </pre>
            ))}
          </SubSection>
        )}

        {data.revision_hints?.length > 0 && (
          <SubSection title={t('extraction_revisions', lang)}>
            <ul className="ml-4 list-disc">
              {data.revision_hints.map((r, i) => (
                <li key={i}>{r.text}</li>
              ))}
            </ul>
          </SubSection>
        )}
      </div>
    </details>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SubSection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{title}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function KeyValueTable({ rows }) {
  return (
    <table className="w-full text-left">
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-t border-border/30">
            <td className="w-2/5 py-1 pr-2 align-top text-[11px] font-medium text-muted-foreground">
              {row[0]}
            </td>
            <td className="py-1 align-top">{row[1]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
